import { useRef, useState, type FormEvent } from "react";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { Drawer } from "../ui/Dialog.js";
import type { MetronomePracticePreset } from "../practiceTodos.js";
import {
  MetronomeControls,
  MetronomeProvider,
  useMetronome,
  useMetronomeSpaceShortcut,
} from "./MetronomeBase.js";

export interface TutorMetronomePracticeTask {
  title: string;
  description: string | null;
  suggestedMinutes: number | null;
  metronome: Omit<MetronomePracticePreset, "sourceLessonId">;
}

interface Props {
  onClose: () => void;
  assignment?: {
    learnerName: string;
    domain: "private" | "school";
    onSave: (task: TutorMetronomePracticeTask) => Promise<void>;
    onSaved?: () => void | Promise<void>;
  };
}

export function TutorMetronome(props: Props) {
  return (
    <MetronomeProvider>
      <TutorMetronomeContent {...props} />
    </MetronomeProvider>
  );
}

function TutorMetronomeContent({ onClose, assignment }: Props) {
  const tapButtonRef = useRef<HTMLButtonElement>(null);
  const { bpm, signature, beatModes, speedUp, subdivision, subdivisionModes } = useMetronome();
  const [title, setTitle] = useState("Metronome practice");
  const [notes, setNotes] = useState("");
  const [minutes, setMinutes] = useState("5");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [refreshProblem, setRefreshProblem] = useState(false);
  useMetronomeSpaceShortcut({ enabled: true, tapTempo: true });

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!assignment || saving || saved) return;
    setSaving(true);
    setProblem(null);
    try {
      await assignment.onSave({
        title: title.trim() || "Metronome practice",
        description: notes.trim() || null,
        suggestedMinutes: Number(minutes) || null,
        metronome: {
          bpm, signature: signature.label, beatModes: [...beatModes],
          subdivision: { id: subdivision.id, modes: subdivisionModes.map((row) => [...row]) },
          speedUp: speedUp.enabled ? {
            increaseBpm: speedUp.increaseBpm,
            everyBars: speedUp.everyBars,
            targetBpm: speedUp.targetBpm,
          } : null,
        },
      });
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not set the metronome practice.");
      return;
    } finally {
      setSaving(false);
    }
    setSaved(true);
    try {
      await assignment.onSaved?.();
    } catch {
      setRefreshProblem(true);
    }
  }

  return (
    <Drawer
      title="Metronome"
      eyebrow={assignment?.learnerName}
      description="Tap in time to find the tempo, then press play to use it in your lesson."
      onClose={onClose}
      size="large"
      className="tutor-metronome"
      busy={saving}
      initialFocusRef={tapButtonRef}
    >
      <MetronomeControls tapButtonRef={tapButtonRef} />
      {assignment && (saved ? (
        <>
          <ActionConfirmation
            variant="success"
            title="Metronome practice set"
            description={<>It is now in {assignment.learnerName}&rsquo;s {assignment.domain === "school" ? "School practice record" : "practice tasks"} and will open with this preset.</>}
            action={<button type="button" onClick={onClose}>Done</button>}
          />
          {refreshProblem && <p className="notice small" role="status">Practice was saved, but the lesson view could not refresh. Close and reopen the lesson to see the new task.</p>}
        </>
      ) : (
        <details className="tutor-metronome-practice">
          <summary>Set as practice</summary>
          <form className="lesson-tool-form" onSubmit={save}>
            <p className="small muted">Assign the starting tempo, speed-up settings, time signature, subdivision and click accents to {assignment.learnerName}. Students open with this click pattern and can adjust the settings for their own practice.</p>
            {problem && <div className="error" role="alert">{problem}</div>}
            <label className="lesson-tool-field">
              <span>Task title</span>
              <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} />
            </label>
            <label className="lesson-tool-field">
              <span>{assignment.domain === "school" ? "Practice notes shown on the School record" : "Practice notes shown to the student"}</span>
              <textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="E.g. Keep the backbeat steady. Increase by 5 BPM when comfortable." />
            </label>
            <label className="lesson-tool-field compact">
              <span>Suggested minutes per practice</span>
              <input type="number" min="1" max="480" value={minutes} onChange={(event) => setMinutes(event.target.value)} />
            </label>
            <button type="submit" disabled={saving}>{saving ? "Setting practice…" : assignment.domain === "school" ? "Add to School practice" : "Add to practice tasks"}</button>
          </form>
        </details>
      ))}
    </Drawer>
  );
}
